/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
/* eslint-disable import/no-unresolved */
/**
 * @name Captable Hook
 * @memberof module:common/hooks
 * @type {ReactHook}
 * @return {React} Captable Hook
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isUndefined } from 'lodash';
import { isEmpty } from 'validate.js';
import {
  ApiService,
  CapTableDetailsModel,
  CapTablesService,
  CashFlowLedgerItem,
  CustomBreakpoints,
  FundOwnershipCapTableDetailsModel,
  FundOwnershipDetailModel,
  ProFormaCapTableModel,
  SecurityModel,
} from 'api';
import type { AcceptAIFieldRequest } from 'api/models/AcceptAIFieldRequest';
import type { DismissAIFieldRequest } from 'api/models/DismissAIFieldRequest';
import { ERROR_400, ERROR_403, ERROR_500 } from 'common/config/api';
import { ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY } from 'common/constants/services/companies';
import { useStore } from 'common/store';
import { distributeDirtyPayload } from 'components/CellAnnotationValueViewer';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import {
  splitDirtyForCflSave,
  splitDirtyForFodSave,
  useCellAnnotationStore,
} from 'store/useCellAnnotationStore';
import { CaptableService } from '..';
import { AttributeType, useRetrieveAttributeMapper } from './attributeMapper';
import { useResponse } from './useResponse';
import { captableAction, globalAction } from '../../common/actions';
import { CapTableInfo } from '../../common/types/capTable';
import { useAttributeMapperStore } from '../../store/useAttributeMapperStore';
import { extractServerErrorMessage } from '../../utilities';
import convertCamelCaseToSnakeCase from '../../utilities/convertCamelCaseToSnakeCase/convertCamelCaseToSnakeCase';
import SheetAttributeManager from '../sheet-attribute-manager/SheetAttributeManager';

/**
 * @function
 * @name useGetCaptableInfo
 * @memberof Captable Hook
 * @description Hook to fetch captable info from captable service
 * @param {integer} dataId the captable id
 * @return {object} structure for store context.
 */

export const useGetCaptableInfo = () => {
  const [globalStore, dispatch] = useStore();
  const [capTableInfo, setUpdatedCapTableInfo] = useState<CapTableInfo>({});
  const inFlightIdsRef = useRef<Set<number>>(new Set());

  const { processErrorResponse, errorNotification } = useResponse();
  const { setAttributeMapperManager, clearAttributeMapperManager } = useAttributeMapperStore();
  const [retrieveAttributeMapper] = useRetrieveAttributeMapper();

  const getCapTableInfo = useCallback(
    async (newCaptableId, updateGlobalStore = true) => {
      if (newCaptableId) {
        const inFlightIds = inFlightIdsRef.current;
        if (inFlightIds.has(newCaptableId)) {
          return;
        }
        inFlightIds.add(newCaptableId);
        dispatch(globalAction.showLoadingProgress(true));
        dispatch(captableAction.setCaptableInfo({}));

        const defaultErrorMessage = 'An error occurred while loading the Cap Table information';

        try {
          const captSvc = new CaptableService();
          const capTableInfo = (await captSvc.dataById(newCaptableId)) as unknown as CapTableInfo;
          if (!isEmpty(capTableInfo)) {
            if (updateGlobalStore) {
              dispatch(
                captableAction.setCaptableInfo({
                  ...globalStore.capTableInfo,
                  ...(capTableInfo as unknown as Partial<CapTableInfo>),
                })
              );
            }
            setUpdatedCapTableInfo(capTableInfo);

            if (capTableInfo.sheet_attribute_mapping) {
              const retrievedMapper = await retrieveAttributeMapper(
                capTableInfo.sheet_attribute_mapping,
                AttributeType.CAP_TABLE
              );
              setAttributeMapperManager(new SheetAttributeManager(retrievedMapper));
            } else {
              clearAttributeMapperManager();
            }
          } else {
            errorNotification(defaultErrorMessage);
          }
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'get the Cap Table information',
          });
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
          inFlightIds.delete(newCaptableId);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, globalStore.capTableInfo]
  );

  return [capTableInfo, getCapTableInfo];
};

export const useAcceptAIFields = () => {
  const [globalStore] = useStore();
  const [, getCapTableInfo] = useGetCaptableInfo();
  const { processErrorResponse } = useResponse();

  const acceptAIFields = useCallback(
    async (securityId: number, payload: AcceptAIFieldRequest) => {
      const defaultErrorMessage = 'An error occurred while accepting AI fields';

      try {
        await CapTablesService.apiCapTablesSecuritiesAcceptAiFieldsPartialUpdate(String(securityId), payload);
        await getCapTableInfo(globalStore.captableInfo?.id);
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'accept AI fields',
        });
        throw error;
      }
    },
    [getCapTableInfo, globalStore.captableInfo?.id, processErrorResponse]
  );

  return { acceptAIFields };
};

export const useAcceptAllAIFields = () => {
  const [globalStore] = useStore();
  const [, getCapTableInfo] = useGetCaptableInfo();
  const { processErrorResponse } = useResponse();

  const acceptAllAIFields = useCallback(
    async (capTableId: number) => {
      const defaultErrorMessage = 'An error occurred while accepting all AI fields';

      try {
        await CapTablesService.apiCapTablesAcceptAllAiFieldsPartialUpdate(String(capTableId));
        await getCapTableInfo(globalStore.captableInfo?.id);
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'accept all AI fields',
        });
        throw error;
      }
    },
    [getCapTableInfo, globalStore.captableInfo?.id, processErrorResponse]
  );

  return { acceptAllAIFields };
};

export const useDismissAIFields = () => {
  const [globalStore] = useStore();
  const [, getCapTableInfo] = useGetCaptableInfo();
  const { processErrorResponse } = useResponse();

  const dismissAIFields = useCallback(
    async (securityId: number, payload: DismissAIFieldRequest) => {
      const defaultErrorMessage = 'An error occurred while dismissing AI fields';

      try {
        await CapTablesService.apiCapTablesSecuritiesDismissAiFieldsPartialUpdate(String(securityId), payload);
        await getCapTableInfo(globalStore.captableInfo?.id);
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'dismiss AI fields',
        });
        throw error;
      }
    },
    [getCapTableInfo, globalStore.captableInfo?.id, processErrorResponse]
  );

  return { dismissAIFields };
};

/**
 * @function
 * @name useGetBreakpointAnalysis
 * @description Get the breakpoint analysis for a specific Cap Table by its id
 * @return {array}
 */
export const useGetBreakpointAnalysis = () => {
  const [globalStore, dispatch] = useStore();
  const [capTableInfo, setUpdatedCapTableInfo] = useState<CapTableInfo | string>({});

  const { processErrorResponse, errorNotification } = useResponse();

  const getCapTableBreakpoints = useCallback(
    async newCaptableId => {
      if (newCaptableId) {
        dispatch(globalAction.showLoadingProgress(true));

        const defaultErrorMessage = 'An error occurred while loading the Breakpoint Analysis information';

        try {
          const captSvc = new CaptableService();
          const breakpointAnalysis = (await captSvc.getAllBreakpointsData(
            newCaptableId
          )) as unknown as CapTableDetailsModel;

          if (breakpointAnalysis) {
            let prevCaptableInfo = { ...globalStore.captableInfo };

            if (isEmpty(prevCaptableInfo)) {
              const captSvc = new CaptableService();
              const updatedCaptableInfo = await captSvc.dataById(newCaptableId);

              if (updatedCaptableInfo) {
                prevCaptableInfo = { ...updatedCaptableInfo };
              }
            }

            const captable = {
              ...prevCaptableInfo,
              waterfall_breakpoints: breakpointAnalysis.waterfall_breakpoints,
              custom_breakpoints: breakpointAnalysis.custom_breakpoints,
            };
            dispatch(captableAction.setCaptableInfo(captable));
            setUpdatedCapTableInfo(captable);
          } else {
            errorNotification(defaultErrorMessage);
          }
        } catch (error: any) {
          if (error.status === 403) {
            setUpdatedCapTableInfo(ERROR_403);
          } else {
            processErrorResponse({ error, defaultErrorMessage });
          }
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, globalStore.captableInfo?.id]
  );

  return { capTableInfo, getCapTableBreakpoints };
};

export const useUpdateCapTableAndSecuritiesInfo = () => {
  const [data, setData] = useState({});

  const captSvc = new CaptableService();

  const [globalStore, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  const getErrorInfoFromResponse = (error: any, defaultErrorMessage: string) => {
    let errorMessage = defaultErrorMessage;
    const servicePath = error.response.req.url.split('/').pop();
    if (servicePath === 'securities') {
      if (error.response.status === Number(ERROR_500)) {
        const securitiesError = JSON.parse(error.response.text).securities;
        if (securitiesError) {
          [errorMessage] = securitiesError;
        }
      }
      if (error.response.status === Number(ERROR_400)) {
        const securitiesNameError = JSON.parse(error.response.text).name;
        if (securitiesNameError) {
          [errorMessage] = securitiesNameError;
        }
      }
    }
    return {
      errorMessage,
      servicePath,
    };
  };

  // React Query Client
  const queryClient = useQueryClient();

  const updateCapTableData = async (
    captableId: number,
    securities: SecurityModel[],
    captableInfo: CapTableDetailsModel,
    updateGlobalStore: boolean
  ) => {
    if (captableId) {
      dispatch(globalAction.showLoadingProgress(true));

      let defaultErrorMessage = 'An error occurred while updating Cap Table information';

      try {
        let updatedCap = { ...globalStore.captableInfo };
        let updatedSecurities = [...(globalStore.captableInfo?.securities || [])];

        // Fold captable-scoped cell-annotation edits into the PUT body so the
        // backend persists them in the same transaction as the cap-table save.
        // Wire shape: { cell_annotations: { field_alias: { annotation_type: entry } } }.
        const dirtyAnnotations = useCellAnnotationStore.getState().collectDirtyPayload();
        const { capTableByParentId } = distributeDirtyPayload(dirtyAnnotations);
        const captableAnnotationsWire = capTableByParentId[String(captableId)];

        if (!isUndefined(captableInfo) || captableAnnotationsWire) {
          const captableBody: any = { ...(captableInfo ?? {}) };
          if (captableAnnotationsWire) {
            captableBody.cell_annotations = captableAnnotationsWire;
          }
          updatedCap = await captSvc.updateCaptableById(captableId, captableBody);
        }

        if (securities?.length > 0) {
          defaultErrorMessage = 'An error occurred while updating Cap Table securities';

          const snakeCased = securities.map(item => ({
            ...item,
            citation_references: convertCamelCaseToSnakeCase(item.citation_references),
          }));

          updatedSecurities = (await captSvc.updateSecuritiesById(captableId, snakeCased)) as SecurityModel[];
          updatedCap = await captSvc.getCaptableById(captableId);
        }

        setData({ ...updatedCap, securities: updatedSecurities });
        if (updateGlobalStore) {
          dispatch(captableAction.setCaptableInfo({ ...globalStore.capTableInfo, ...updatedCap }));
        }
        useCellAnnotationStore.getState().clearDirty('captable:');
        successNotification('Cap Table information updated successfully');
        setAction(false);
      } catch (error: any) {
        const { errorMessage, servicePath } = getErrorInfoFromResponse(error, defaultErrorMessage);
        const action = servicePath === 'securities' ? 'update Cap Table securities' : 'update Cap Table information';
        processErrorResponse({
          error,
          defaultErrorMessage: errorMessage,
          action,
        });
      } finally {
        // Invalidate the Allocation Values query to refetch the data with the new values
        queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });

        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  };

  return [data, updateCapTableData];
};

/**
 * @function
 * @name useUpdateCaptableInfo
 * @memberof Captable Hook
 * @description Hook to update captable info from captable service
 * @param {integer} dataId the captable id
 * @param {object} captableInfo captable structure data
 * @return {object} structure for store context.
 */
export const useUpdateCaptableInfo = ({ captableId, captableInfo }) => {
  const [globalStore, dispatch] = useStore();
  const [data, setData] = useState<CapTableDetailsModel>({} as CapTableDetailsModel);
  const prevCaptableInfo = useRef(globalStore.captableInfo);

  const { processErrorResponse, errorNotification, successNotification } = useResponse();

  const updateCapTableData = useCallback(
    async (newCaptableId, newCaptableInfo) => {
      if (newCaptableId && newCaptableInfo) {
        dispatch(globalAction.showLoadingProgress(true));

        const defaultErrorMessage = 'An error occurred while updating Cap Table information';

        try {
          const captSvc = new CaptableService();
          const captableInfo = (await captSvc.updateCaptableById(
            newCaptableId,
            newCaptableInfo
          )) as unknown as CapTableDetailsModel;
          if (!isEmpty(captableInfo)) {
            dispatch(
              captableAction.setCaptableInfo({
                ...prevCaptableInfo.current,
                ...(captableInfo as unknown as Partial<CapTableInfo>),
              })
            );
            setData(captableInfo);
            successNotification('Cap Table updated successfully');
          } else {
            errorNotification(defaultErrorMessage);
          }
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'update the Cap Table',
          });
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, prevCaptableInfo]
  );

  useEffect(() => {
    updateCapTableData(captableId, captableInfo);
  }, [captableId, captableInfo, updateCapTableData]);

  return [data, updateCapTableData];
};

/**
 * @function
 * @name useUpdateCaptableSecurities
 * @memberof Captable Hook
 * @description Hook to update captable info from Cap Table service
 * @param {INTEGER} dataId the captable id
 * @param {OBJECT} captableSecurititesInfo captable structure data
 * @return {OBJECT} structure for store context.
 */
export const useUpdateCaptableSecurities = () => {
  const captSvc = new CaptableService();
  const [globalStore, dispatch] = useStore();
  const [data, setData] = useState({});

  const { processErrorResponse, errorNotification, successNotification } = useResponse();

  const updateSecuritiesData = async (newCaptableId: number, newCaptableSecurititesInfo: SecurityModel[]) => {
    dispatch(globalAction.showLoadingProgress(true));

    const defaultErrorMessage = 'An error occurred while updating the Cap Table securities';

    try {
      const captableSecuritiesInfo = await captSvc.updateSecuritiesById(newCaptableId, newCaptableSecurititesInfo);
      if (captableSecuritiesInfo) {
        dispatch(
          captableAction.setCaptableInfo({
            ...globalStore.captableInfo,
            securities: captableSecuritiesInfo,
          })
        );
        setData(captableSecuritiesInfo);
        successNotification('Cap Table securities updated successfully');
      } else {
        errorNotification(defaultErrorMessage);
      }
    } catch (error) {
      processErrorResponse({
        error,
        defaultErrorMessage,
        action: 'update the Cap Table securities',
      });
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  };
  return [{ captableSecuritiesInfo: data }, updateSecuritiesData];
};

export const useUpdateCaptableFundOwnership = () => {
  const [updatedFundOwnership, setUpdatedFundOwnership] = useState({});

  const [globalStore, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const isSecurityUsedInFod = (fundOwnershipDetail: FundOwnershipCapTableDetailsModel[], security: SecurityModel) =>
    fundOwnershipDetail.some(
      (fod: any) =>
        fod.security_id === security.id ||
        security.convertible_notes.map(cn => `${security.name} - ${cn.note_name}`).includes(fod.security_name)
    );

  const updateFundOwnershipData = async (captableId: number, fundOwnershipInfo: FundOwnershipDetailModel) => {
    if (captableId && fundOwnershipInfo) {
      dispatch(globalAction.showLoadingProgress(true));

      // Fold every dirty cell-annotation entry onto the nested write payload.
      // PRE_FX, cross_reference, and any future annotation type all ride on
      // the same `cell_annotations` field on each parent — the backend
      // peeks for a PRE_FX entry to derive the FX host column, then upserts
      // the full map through the standard cell-annotations path.
      const dirtyAnnotations = useCellAnnotationStore.getState().collectDirtyPayload();
      const {
        fodCellAnnotations,
        acquisitionCellAnnotations,
        saleCellAnnotations,
        distributionCellAnnotations,
      } = splitDirtyForFodSave(dirtyAnnotations);
      // A child row carries either a persisted `id` (numeric, used as the
      // bucket key) OR a synthetic key derived from its ref UUID
      // (`ref_<uuid>`, set by `applyPreFxAnnotation` for unsaved rows). Look
      // by id first, then by `ref_<rowRef>` so a fresh row's annotations
      // ship in the same payload that creates it.
      const lookupChildEntry = <T,>(
        bucket: Record<string, T>,
        id: number | string | null | undefined,
        rowRef: string | null | undefined
      ): T | undefined => {
        if (id != null && id !== 0 && id !== '') {
          const byId = bucket[String(id)];
          if (byId !== undefined) {
            return byId;
          }
        }
        return rowRef ? bucket[`ref_${rowRef}`] : undefined;
      };
      const withAnnotations = <T extends object>(row: T, annotations: unknown): T =>
        annotations ? ({ ...row, cell_annotations: annotations } as T) : { ...row };
      const enrichedFundOwnershipInfo: any = { ...fundOwnershipInfo };
      if (Array.isArray(enrichedFundOwnershipInfo.fund_ownership_detail)) {
        // Drop synthetic CFL-only FOD entries (flagged `historical_record:
        // true` by the cap-table serializer). They have no row in the
        // database, including them in the PUT payload would tell the
        // backend to update non-existent records.
        enrichedFundOwnershipInfo.fund_ownership_detail = enrichedFundOwnershipInfo.fund_ownership_detail
          .filter((fod: any) => !fod?.historical_record)
          .map(
            (fod: any) => {
              const enrichedFod: any = withAnnotations(
                fod,
                fod?.id != null ? fodCellAnnotations[String(fod.id)] : undefined
              );

              if (Array.isArray(enrichedFod.acquisitions)) {
                enrichedFod.acquisitions = enrichedFod.acquisitions.map((acq: any) => {
                  const updatedAcq: any = withAnnotations(
                    acq,
                    lookupChildEntry(acquisitionCellAnnotations, acq?.id, acq?.acquisition_ref)
                  );

                  if (Array.isArray(acq?.sales)) {
                    updatedAcq.sales = acq.sales.map((sale: any) =>
                      withAnnotations(
                        sale,
                        lookupChildEntry(saleCellAnnotations, sale?.id, sale?.sale_ref)
                      )
                    );
                  }

                  if (Array.isArray(acq?.cash_distributions)) {
                  // Cash distributions don't have a stable ref field today;
                  // only persisted (id-bearing) rows route here.
                    updatedAcq.cash_distributions = acq.cash_distributions.map((dist: any) =>
                      withAnnotations(
                        dist,
                        lookupChildEntry(distributionCellAnnotations, dist?.id, null)
                      )
                    );
                  }

                  return updatedAcq;
                });
              }
              return enrichedFod;
            }
          );
      }

      try {
        const fundOwnershipResponse = await ApiService.apiCapTablesFundOwnershipUpdate(
          captableId,
          enrichedFundOwnershipInfo
        );

        const { fund_ownership_detail } = fundOwnershipResponse;

        // The response includes the fields fund_ownership_id, fund_id, and security_id,
        // but the frontend is expecting fund_ownership, fund, and security, respectively.
        const tmpFundOwnershipDetail = fund_ownership_detail.map((fod: any) => ({
          ...fod,
          fund: fod.fund_id,
          security: fod.security_id,
          fund_ownership: fod.fund_ownership_id,
        }));

        const tmpFundOwnershipInfo = {
          ...globalStore.captableInfo.fund_ownership,
          ...fundOwnershipResponse,
          fund_ownership_detail: tmpFundOwnershipDetail,
        };

        dispatch(
          captableAction.setCaptableInfo({
            ...globalStore.captableInfo,
            securities: globalStore.captableInfo.securities.map((security: SecurityModel) => ({
              ...security,
              is_used_in_fod: isSecurityUsedInFod(tmpFundOwnershipDetail, security),
            })),
            fund_ownership: tmpFundOwnershipInfo,
          })
        );

        setAction(false);
        // Clear dirty across every parent type the FOD writer flushes — the
        // FOD payload carries cross-reference for FOD parents and Pre-FX for
        // each child writable. clearDirty is per-prefix; we call it once per
        // type to avoid clobbering unrelated cap-table entries.
        const annotationStore = useCellAnnotationStore.getState();
        annotationStore.clearDirty('fundownershipdetail:');
        annotationStore.clearDirty('sharesacquisition:');
        annotationStore.clearDirty('sharessale:');
        annotationStore.clearDirty('cashdistribution:');
        successNotification('Fund Ownership was updated successfully');
        setUpdatedFundOwnership(tmpFundOwnershipInfo);
      } catch (error: any) {
        const defaultErrorMessage =
          error.response?.body?.detail ??
          Object.values(JSON.parse(error.response?.text))[0][0] ??
          'An error occurred while updating the Cap Table Fund Ownership';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'update the Cap Table Fund Ownership',
        });
      } finally {
        // Invalidate the Allocation Values query to refetch the data with the new values
        queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });

        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  };
  return { updatedFundOwnership, updateFundOwnershipData };
};

export const useCaptableCashFlowLedger = () => {
  const [cashFlowLedger, setCashFlowLedger] = useState<CashFlowLedgerItem[]>([]);

  const [globalStore, dispatch] = useStore();
  const captableInfoRef = useRef(globalStore.captableInfo);

  useEffect(() => {
    captableInfoRef.current = globalStore.captableInfo;
  }, [globalStore.captableInfo]);

  const { setAction } = useContext(UnsavedChangesContext);
  const { processErrorResponse, successNotification } = useResponse();

  const getCashFlowLedgerErrorMessage = (error: any): string | null => {
    const parsePayload = (payload: unknown): string | null => {
      if (!payload) {
        return null;
      }

      if (typeof payload === 'string') {
        try {
          const parsed = JSON.parse(payload) as Record<string, unknown>;
          return extractServerErrorMessage(parsed) ?? payload;
        } catch {
          return payload;
        }
      }

      if (Array.isArray(payload)) {
        const [firstValue] = payload;
        if (typeof firstValue === 'string') {
          return firstValue;
        }
        if (typeof firstValue === 'object' && firstValue) {
          return extractServerErrorMessage(firstValue as Record<string, unknown>);
        }
      }

      if (typeof payload === 'object') {
        return extractServerErrorMessage(payload as Record<string, unknown>);
      }

      return null;
    };

    return (
      parsePayload(error?.response?.data) ??
      parsePayload(error?.response?.body) ??
      parsePayload(error?.response?.text) ??
      (typeof error?.response?.statusText === 'string' ? error.response.statusText : null)
    );
  };

  const sortCashFlowsByDate = useCallback(
    (flows: CashFlowLedgerItem[]) =>
      [...flows].sort((a, b) => {
        const dateA = a.date_of_transaction ? new Date(a.date_of_transaction).getTime() : 0;
        const dateB = b.date_of_transaction ? new Date(b.date_of_transaction).getTime() : 0;
        return dateA - dateB;
      }),
    []
  );

  const fetchCashFlowLedger = useCallback(
    async (captableId?: number) => {
      if (!captableId) {
        return [];
      }

      dispatch(globalAction.showLoadingProgress(true));

      const defaultErrorMessage = 'An error occurred while loading the Cash Flow Ledger';

      try {
        const cashFlowLedgerResponse = await ApiService.apiCapTablesCashFlowLedgerList(captableId);
        const ledgerItems = sortCashFlowsByDate(cashFlowLedgerResponse?.items ?? []);

        const currentCaptableInfo = captableInfoRef.current ?? {};
        dispatch(
          captableAction.setCaptableInfo({
            ...currentCaptableInfo,
            cash_flow_ledger: ledgerItems,
          })
        );
        setCashFlowLedger(ledgerItems);
        return ledgerItems;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'load the Cash Flow Ledger',
        });
        return [];
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [dispatch, processErrorResponse, sortCashFlowsByDate]
  );

  const updateCashFlowLedger = useCallback(
    async (captableId: number | undefined, ledgerItems: CashFlowLedgerItem[]) => {
      if (!captableId || !ledgerItems) {
        return [];
      }

      dispatch(globalAction.showLoadingProgress(true));

      const defaultErrorMessage = 'An error occurred while updating the Cash Flow Ledger';

      // Fold every dirty cell-annotation entry onto its matching CFL item by
      // id. Virtual rows are filtered out by the reverse parser before they
      // reach here; new (no-id) rows can't be annotated by the UI yet so
      // they're a clean miss. Backend's CashFlowLedgerItemSerializer peeks
      // for a PRE_FX entry to derive the FX host column, then upserts the
      // whole map through the standard cell-annotations path.
      const dirtyAnnotations = useCellAnnotationStore.getState().collectDirtyPayload();
      const { cflCellAnnotations } = splitDirtyForCflSave(dirtyAnnotations);

      try {
        const itemsWithCapTable = ledgerItems.map(item => {
          const baseItem = { ...item, cap_table: captableId };
          const annotations = item.id != null ? cflCellAnnotations[String(item.id)] : undefined;
          return annotations ? { ...baseItem, cell_annotations: annotations } : baseItem;
        });
        const cashFlowLedgerResponse = await ApiService.apiCapTablesCashFlowLedgerBulkSaveCreate(captableId, {
          items: itemsWithCapTable,
        });

        const updatedLedger = sortCashFlowsByDate(cashFlowLedgerResponse?.items ?? ledgerItems);

        const currentCaptableInfo = captableInfoRef.current ?? {};
        dispatch(
          captableAction.setCaptableInfo({
            ...currentCaptableInfo,
            cash_flow_ledger: updatedLedger,
          })
        );

        setAction(false);
        // Clear dirty for CFL Pre-FX entries. The annotation store keys live
        // under the `cashflowledgeritem:` prefix; clearing only that prefix
        // leaves any unrelated cap-table entries (e.g. concurrent FOD edits)
        // untouched.
        useCellAnnotationStore.getState().clearDirty('cashflowledgeritem:');
        successNotification('Cash Flow Ledger updated successfully');
        setCashFlowLedger(updatedLedger);
        return updatedLedger;
      } catch (error) {
        const errorMessage = getCashFlowLedgerErrorMessage(error);
        processErrorResponse({
          error,
          defaultErrorMessage: errorMessage ?? defaultErrorMessage,
          action: 'update the Cash Flow Ledger',
        });
        throw error;
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [dispatch, processErrorResponse, setAction, sortCashFlowsByDate, successNotification]
  );

  return { cashFlowLedger, fetchCashFlowLedger, updateCashFlowLedger };
};

/**
 * @function
 * @name useUpdateCaptableCustomBreakpoints
 * @memberof Captable Hook
 * @description Hook to update captable custom breakpoints from Cap Table service
 * @param {INTEGER} captableId the captable id
 * @param {OBJECT} breakpointsData captable custom BPs data
 */
export const useUpdateCaptableCustomBreakpoints = () => {
  const [updatedCustomBreakpoints, setUpdatedCustomBreakpoints] = useState({});

  const [globalStore, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const updateCustomBreakpoints = useCallback(
    async (captableId: number, breakpointsData: CustomBreakpoints) => {
      if (captableId && breakpointsData) {
        dispatch(globalAction.showLoadingProgress(true));

        try {
          const breakpointsResponse = await ApiService.apiCapTablesAllBreakpointsUpdate(captableId, breakpointsData);

          const hasUnconfiguredCustomBreakpoints =
            breakpointsResponse.use_custom_breakpoints === true &&
            (breakpointsResponse.custom_breakpoints?.breakpoints ?? []).some((bp: any) =>
              (bp.series ?? []).some((s: any) => s.value === null)
            );

          dispatch(
            captableAction.setCaptableInfo({
              ...globalStore.captableInfo,
              ...breakpointsResponse,
              has_unconfigured_custom_breakpoints: hasUnconfiguredCustomBreakpoints,
            })
          );

          setAction(false);
          successNotification('Breakpoints were updated successfully');
          setUpdatedCustomBreakpoints(breakpointsResponse);
        } catch (error) {
          const defaultErrorMessage = 'An error occurred while updating the breakpoints';
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'update the Cap Table breakpoints',
          });
        } finally {
          // Invalidate the Allocation Values query to refetch the data with the new values
          queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });

          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    [dispatch, globalStore.captableInfo, setAction, successNotification, processErrorResponse, queryClient]
  );
  return { updatedCustomBreakpoints, updateCustomBreakpoints };
};

export const useCreateCapTableProforma = () => {
  const [proformaInfo, setProformaInfo] = useState<ProFormaCapTableModel>({} as ProFormaCapTableModel);

  const captSvc = new CaptableService();

  const [, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const createProforma = async (proformaInfoPayload: ProFormaCapTableModel) => {
    if (proformaInfoPayload) {
      dispatch(globalAction.showLoadingProgress(true));

      try {
        const response = await captSvc.createCapTableProforma(proformaInfoPayload);
        setAction(false);
        setProformaInfo(response as unknown as ProFormaCapTableModel);
        successNotification('The new proforma was created successfully');
      } catch (error) {
        const defaultErrorMessage = 'An error occurred while creating the new proforma';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'create new proforma',
        });
      } finally {
        // Invalidate the Allocation Values query to refetch the data with the new values
        queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });

        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  };
  return [proformaInfo, setProformaInfo, createProforma];
};

export const useGetPrimaryCaptableByCompanyDate = () => {
  const [, dispatch] = useStore();
  const [capTableInfo, setPrimaryCaptable] = useState<CapTableDetailsModel>({} as CapTableDetailsModel);

  const { processErrorResponse, errorNotification } = useResponse();

  const getPrimaryCaptable = useCallback(
    async (companyDate: string) => {
      if (companyDate) {
        dispatch(globalAction.showLoadingProgress(true));

        const defaultErrorMessage = 'An error occurred while loading the Cap Table information';

        try {
          const captSvc = new CaptableService();
          const capTableInfo = await captSvc.getPrimaryCaptableByCompanyDate(companyDate);

          if (!isEmpty(capTableInfo)) {
            setPrimaryCaptable(capTableInfo as unknown as CapTableDetailsModel);
            dispatch(captableAction.setCaptableInfo(capTableInfo as unknown as CapTableDetailsModel));
          } else {
            errorNotification(defaultErrorMessage);
          }
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'get the Cap Table information',
          });
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  return [capTableInfo, getPrimaryCaptable];
};
